/* eslint-disable import/prefer-default-export */
import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate,
  FormTemplateOption,
  FormTemplateSection,
} from 'data/template/template.types';
import { Filters } from 'data/filters/filters.types';
import { FilterGroup } from 'data/filterGroup/filterGroup.types';

export type ListenersIpAddressPortsType = {
  [key:string]: string[],
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
  }

  return clonedTemplate;
}

export function adaptTemplateForType(
  template: FormTemplate,
  typeFieldValue: string,
): string {
  if (template == null) {
    return template;
  }
  const valueField = (template.main as FormTemplateSection).fields.value;

  switch (typeFieldValue) {
    case 'USER-PACKET-CLASS':
      valueField.type = 'number';
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'Interval',
            intervals: [[0, 15]],
            errorMessage: 'Must be between 0 - 15. Please re-enter.',
          },
          {
            type: 'regexp',
            regexp: ['digits'],
            errorMessage: 'The value is not a number.',
          },
        ],
      };
      break;
    case 'DESTINATION-ROUTE-CLASS':
    case 'SOURCE-ROUTE-CLASS':
    case 'TOS':
    case 'IGMP-TYPE':
    case 'ICMP-TYPE':
    case 'ICMP-CODE':
      valueField.type = 'number'
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'Interval',
            intervals: [[0, 255]],
            errorMessage: 'Must be between 0 - 255. Please re-enter.',
          },
          {
            type: 'regexp',
            regexp: ['digits'],
            errorMessage: 'The value is not a number.',
          },
        ],
      };
      break;
    case 'SOURCEADDRESS':
    case 'DESTINATIONADDRESS':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'customFunction',
            customValidationFunction: 'addressValidator',
            errorMessage: 'The value shall be in the following format: [{IP} {Wildcard}] or [host {IP}] or [any].',
          },
        ],
      };
      break;
    case 'SOURCEQUALIFIER':
    case 'DESTINATIONQUALIFIER':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'customFunction',
            customValidationFunction: 'qualifierValidator',
            errorMessage: 'The value shall be in the following format:lt port (end port) or gt port (end port) or eq port (end port) or ne port (end port) or range port (end port). End port is optional. The port number shall be in 1 to 65535.',
          },
        ],
      };
      break;
    case 'PRECEDENCE':
      valueField.type = 'number';
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'Interval',
            intervals: [[0, 7]],
            errorMessage: 'Must be between 0 - 7. Please re-enter.',
          },
          {
            type: 'regexp',
            regexp: ['digits'],
            errorMessage: 'The value is not a number.',
          },
        ],
      };
      break;
    case 'DSFIELD':
      valueField.type = 'number';
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'Interval',
            intervals: [[0, 63]],
            errorMessage: 'Must be between 0 - 63. Please re-enter.',
          },
          {
            type: 'regexp',
            regexp: ['digits'],
            errorMessage: 'The value is not a number.',
          },
        ],
      };
      break;
    case 'IPADDRESS':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'IPAddress',
            invalidIPs: ['255.255.255.255'],
            errorMessage: 'Invalid IPv4 address',
          },
        ],
      };
      break;
    case 'IPADDRESS:PORT':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'customFunction',
            customValidationFunction: 'filtersIpPortValidator',
            errorMessage: 'IP address, Transport and Port combination not unique. Please review.',
          },
        ],
      };
      break;
    case 'IPADDRESS6':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'customFunction',
            customValidationFunction: 'filtersIpv6Validator',
            errorMessage: 'Invalid IPv6 address.',
          },
        ],
      };
      break;
    case 'IPADDRESS6:PORT':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'customFunction',
            customValidationFunction: 'filtersIpv6PortValidator',
            errorMessage: 'Invalid \'IPv6 address and Port\' format. Valid format examples: \'[2080::221:28ff:fe00:d8d8]:1234\'',
          },
        ],
      };
      break;
    case 'PORT':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'Interval',
            intervals: [[0, 65535]],
            errorMessage: 'Must be between 0 - 65535. Please re-enter.',
          },
          {
            type: 'regexp',
            regexp: ['digits'],
            errorMessage: 'The value is not a number.',
          },
        ],
      };
      break;
    case 'TRAFFIC-CLASS':
      valueField.validation = {
        required: true,
        validators: [
          {
            type: 'length',
            length: 40,
          },
        ],
      };
      break;
    case 'IP-FRAG-OFFSET':
      valueField.label = 'IP Flag Offset'
      valueField.type = 'select';
      valueField.initial= 'eq 0';
      valueField.options = [
        {
            label: 'Equals 0',
            value: 'eq 0',
        },
        {
            label: 'Equals 1',
            value: 'eq 1',
        },
        {
          label: 'Greater than 1',
          value: 'gt 1',
        },
      ];
      valueField.optionsFirstTranslatable = true;
      valueField.validation = { required: false };
      typeFieldValue = 'eq 0';
      break;
    case 'LOCAL':
      valueField.label = 'Local';
      valueField.type = 'select';
      valueField.initial= 'TRUE';
      valueField.options = [
        {
            label: 'True',
            value: 'TRUE',
        },
        {
            label: 'False',
            value: 'FALSE',
        },
      ];
      valueField.optionsFirstTranslatable = true;
      valueField.validation = { required: false };
      typeFieldValue = 'TRUE';
      break;
    case 'COLOR':
      valueField.label = 'Color';
      valueField.type = 'select';
      valueField.initial= 'GREEN';
      valueField.options = [
        {
            label: 'GREEN',
            value: 'GREEN',
        },
        {
            label: 'YELLOW',
            value: 'YELLOW',
        },
        {
          label: 'RED',
          value: 'RED',
        },
      ];
      valueField.optionsFirstTranslatable = true;
      valueField.validation = { required: false };
      typeFieldValue = 'GREEN';
      break;
    case 'PROTOCOL':
      valueField.label = 'Protocol';
      valueField.type = 'select';
      valueField.initial= 'IP';
      valueField.options = [
        {
            label: 'IP',
            value: 'IP',
        },
        {
            label: 'TCP',
            value: 'TCP',
        },
        {
          label: 'UDP',
          value: 'UDP',
        },
        {
          label: 'IGMP',
          value: 'IGMP',
        },
        {
          label: 'ICMP',
          value: 'ICMP',
        },
      ];
      valueField.optionsFirstTranslatable = true;
      valueField.validation = { required: false };
      typeFieldValue = 'IP';
      break;
    case 'IP-FLAGS':
      (template.main as FormTemplateSection).metadata.layout?.rows?.splice(1, 0, ['VALUELABEL']);
      (template.main as FormTemplateSection).metadata.layout?.rows?.splice(2, 0, ['MORE_FRAGMENTS', 'DONT_FRAGMENT', 'RESERVED']);
      (template.main as FormTemplateSection).metadata.layout?.rows[3]?.splice(0, 1);
      valueField.validation = { required: false };
      break;
    case 'TCP-FLAGS':
      (template.main as FormTemplateSection).metadata.layout?.rows?.splice(1, 0, ['VALUELABEL']);
      (template.main as FormTemplateSection).metadata.layout?.rows?.splice(2, 0, ['ACK', 'FIN', 'PUSH']);
      (template.main as FormTemplateSection).metadata.layout?.rows?.splice(3, 0, ['RST', 'SYN', 'URGENT']);
      (template.main as FormTemplateSection).metadata.layout?.rows[4]?.splice(0, 1);
      valueField.validation = { required: false };
      break;
    default:
      valueField.validation = { required: true };
      break;
  }

  (template.main as FormTemplateSection).fields.value = valueField;
  return typeFieldValue;
}

export function adaptFormDataForView(
  selectedFilters: Filters,
  adaptedFormData: Partial<Filters>
): Partial<Filters> {
  if (selectedFilters.type === 'IP-FLAGS' || selectedFilters.type === 'TCP-FLAGS') {
    selectedFilters.value.split('&').forEach((flag) => {
      adaptedFormData = {
        ...adaptedFormData,
        [flag.replace('-', '_').toUpperCase()]: true,
      }
    })
  }
  return adaptedFormData;
}

export function adaptTemplateOptions(filterGroupList: FilterGroup[], template: FormTemplate,): FormTemplate {
  const filterGroupOptions = getFieldOptions(filterGroupList);
  const clonedTemplated = cloneDeep(template);
  setFieldOptions(clonedTemplated.main as FormTemplateSection, 'fgId', filterGroupOptions);
  return clonedTemplated;
}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }
    Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
      if (!excludingFields.includes(fieldKey)) {
        fieldEntry.readOnly = true;
      }
    });
  });
}

function getFieldOptions(
  fieldList: { fgId: string, id: string }[],
): FormTemplateOption[] {
  return fieldList.map((field) => ({
    label: field.fgId,
    value: field.fgId,
  }));
}

function setFieldOptions(templateSection: FormTemplateSection, field: string, options: FormTemplateOption[]): void {
  templateSection.fields[field]?.options?.push(...options);
}
